import LabelWithToolTip from "@/components/LabelWithTooltip/LabelWithToolTip";
import {
  useDocumentMetadataMutation,
  useUpdateDocumentAssetMutation,
} from "@/hooks/assets";
import { useStore } from "@/services/store";
import Checkbox from "@ingka/checkbox";
import FormField from "@ingka/form-field/FormField";
import InputField from "@ingka/input-field";
import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import Text from "@ingka/text";
import Label from "@ingka/label";
import { useDocumentValidation } from "../../hooks";

type Props = {
  assetId: number;
  allOriginalConfirmation: boolean | null;
  isLockedForOwner: boolean;
};

const Confirmation = ({
  assetId,
  allOriginalConfirmation,
  isLockedForOwner,
}: Props) => {
  const updateDocumentMetadata = useDocumentMetadataMutation(assetId);
  const updateDocumentAsset = useUpdateDocumentAssetMutation(assetId);
  const { documentAssets, document, resetValidationError } = useStore(
    useShallow((state) => ({
      documentAssets: state.documentAssets,
      document: state.documentAssets[assetId],
      resetValidationError: state.resetDocumentValidationError,
    })),
  );
  const [inputValue, setInputValue] = useState(document?.originalName ?? "");
  const originalNameValidation = useDocumentValidation(assetId, "originalName");
  const isCompliantValidation = useDocumentValidation(assetId, "isCompliant");

  useEffect(() => {
    setInputValue(document?.originalName ?? "");
  }, [document?.originalName]);

  return (
    <div className="flex max-h-[12.5rem] flex-col gap-y-space75">
      {!allOriginalConfirmation && (
        <>
          <Label
            htmlFor={`asset-${assetId}-originalName`}
            label={
              <LabelWithToolTip
                labelText="Name of original document"
                toolTipText="If you are uploading an edited version of an existing document, it is mandatory to fill out the name of the original version."
              />
            }
          />
          <FormField {...originalNameValidation}>
            <InputField
              id={`asset-${assetId}-originalName`}
              className="mb-space75 max-w-[17rem]"
              type="text"
              disabled={isLockedForOwner}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              name="originalName"
              onBlur={() => {
                updateDocumentAsset.mutate({
                  [assetId]: {
                    originalName: inputValue.trim(),
                  },
                });
                Object.keys(documentAssets).forEach((key) =>
                  resetValidationError(Number(key), "originalName"),
                );
              }}
            />
          </FormField>
        </>
      )}
      <FormField {...isCompliantValidation}>
        <Checkbox
          disabled={isLockedForOwner}
          className="pt-space50"
          id={`asset-${assetId}-isCompliant`}
          label={
            <div className="flex flex-col">
              <Text tagName="span" className="font-bold">
                I hereby confirm that the document has:
              </Text>
              <ul className="list-disc pl-space150">
                <li>
                  Correct embedded metadata when exporting the file (Title,
                  language and Copying for Accessibility Permission)
                </li>
                <li>
                  Compliant use document structure and hierarchy (headings,
                  lists, links and tables)
                </li>
                <li>A color contrast of a minimum of 4.5:1</li>
                <li>Pagination and guided reading order</li>
                <li>Alternative text for images and graphs</li>
                <li>Readability when resizing to 200% (zoom)</li>
              </ul>
            </div>
          }
          value=""
          checked={document?.isCompliant ?? false}
          onChange={(e) => {
            updateDocumentMetadata.mutate({
              isCompliant: e.target.checked,
            });
            resetValidationError(assetId, "isCompliant");
          }}
          name="isCompliant"
        />
      </FormField>
    </div>
  );
};

export default Confirmation;
